import { Link } from 'react-router-dom';
import { UsersIcon, BuildingsIcon, FolderIcon, DesktopTowerIcon, CircleIcon } from '@phosphor-icons/react';

import { StatCard } from '@/ui/StatCard';
import { SpinnerBlock } from '@/ui/Spinner';
import { Alert } from '@/ui/Alert';
import { Card } from '@/ui/Card';
import { formatAge } from '@/features/admin/format';
import { useAdminStats, type SandboxStatusCounts } from '@/api-models/admin/stats';

// Distinct, theme-aware colors per sandbox state (resolve in light + dark).
const STATUS_CONFIG = [
  { key: 'ready' as const, label: 'Ready', color: 'var(--color-success)' },
  { key: 'provisioning' as const, label: 'Provisioning', color: 'var(--color-info)' },
  { key: 'stopped' as const, label: 'Stopped', color: 'var(--color-fg-tertiary)' },
  { key: 'stopping' as const, label: 'Stopping', color: 'var(--color-warning)' },
  { key: 'archived' as const, label: 'Archived', color: 'var(--color-fg-muted)' },
  { key: 'error' as const, label: 'Error', color: 'var(--color-danger)' },
  { key: 'none' as const, label: 'None', color: 'var(--color-fg-disabled)' },
];

function SandboxStatusBar({ statuses }: { statuses: SandboxStatusCounts }) {
  const total = Object.values(statuses).reduce((a, b) => a + b, 0);

  return (
    <Card padding="md" radius="2xl">
      <h2 className="card-heading mb-4">Sandbox status</h2>

      {total === 0 ? (
        <p className="text-xs text-fg-tertiary">No sandboxes yet.</p>
      ) : (
        <>
          <div className="flex h-3 w-full overflow-hidden rounded-full">
            {STATUS_CONFIG.map(({ key, color }) => {
              const count = statuses[key];
              if (count === 0) return null;
              return (
                <div
                  key={key}
                  style={{ width: `${(count / total) * 100}%`, backgroundColor: color }}
                  className="transition-all duration-300"
                />
              );
            })}
          </div>

          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
            {STATUS_CONFIG.map(({ key, label, color }) => {
              const count = statuses[key];
              if (count === 0) return null;
              const pct = ((count / total) * 100).toFixed(1);
              return (
                <div key={key} className="flex items-center gap-2">
                  <CircleIcon size={10} weight="fill" style={{ color }} />
                  <span className="text-xs text-fg-secondary">
                    {label}{' '}
                    <span className="text-fg-muted">
                      {count} ({pct}%)
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}

export function AdminOverviewPage() {
  const { stats, isLoading } = useAdminStats();

  if (isLoading) {
    return <SpinnerBlock message="Loading platform stats…" />;
  }

  if (!stats) {
    return <div className="text-xs text-fg-muted">Could not load stats.</div>;
  }

  return (
    <>
      <header>
        <h1 className="page-title">Overview</h1>
        <p className="page-subtitle">Platform-wide health and engagement.</p>
      </header>

      <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
        <StatCard icon={<UsersIcon size={24} className="text-brand" />} label="Users" value={stats.total_users} />
        <StatCard icon={<BuildingsIcon size={24} className="text-brand" />} label="Teams" value={stats.total_teams} />
        <StatCard
          icon={<FolderIcon size={24} className="text-brand" />}
          label="Repositories"
          value={stats.total_repositories}
        />
        <StatCard
          icon={<DesktopTowerIcon size={24} className="text-brand" />}
          label="Workspaces"
          value={stats.total_workspaces}
        />
      </div>

      {stats.exhausted_teams > 0 && (
        <Alert variant="warning">
          <strong className="font-medium">{stats.exhausted_teams}</strong>{' '}
          {stats.exhausted_teams === 1 ? 'space has' : 'spaces have'} no usage credit left. Agent work there stops while
          credit enforcement is on.{' '}
          <Link to="/admin/teams" className="underline">
            Review spaces
          </Link>
        </Alert>
      )}

      <SandboxStatusBar statuses={stats.sandbox_statuses} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card padding="md" radius="2xl">
          <h2 className="card-heading mb-4">Top teams</h2>
          {stats.top_teams.length === 0 ? (
            <p className="text-xs text-fg-tertiary">No teams yet.</p>
          ) : (
            <div className="flex flex-col">
              {stats.top_teams.map((row) => (
                <Link
                  key={row.id}
                  to={`/admin/teams/${row.id}`}
                  className="-mx-2 flex items-center justify-between rounded-lg border-b border-line px-2 py-2.5 no-underline last:border-b-0 hover:bg-surface-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium text-fg">{row.name}</p>
                    <p className="truncate text-2xs text-fg-muted">{row.slug}</p>
                  </div>
                  <span className="text-xs text-fg-secondary tabular-nums">
                    {row.workspace_count} <span className="text-fg-muted">workspaces</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card padding="md" radius="2xl">
          <h2 className="card-heading mb-4">Recent signups</h2>
          {stats.recent_signups.length === 0 ? (
            <p className="text-xs text-fg-tertiary">No signups yet.</p>
          ) : (
            <div className="flex flex-col">
              {stats.recent_signups.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between border-b border-line px-1 py-2.5 last:border-b-0"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium text-fg">{u.name || u.email}</p>
                    <p className="truncate text-2xs text-fg-muted">{u.email}</p>
                  </div>
                  <span className="ml-3 whitespace-nowrap text-2xs text-fg-muted tabular-nums">
                    {formatAge(u.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
